import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as contentApi from '@/lib-backend/methods/content';

/**
 * Branding asset roles. The role becomes the stored file name:
 * `branding/{realmId}/{role}.{ext}` — the store inserts the content hash
 * before the extension, so re-uploads never collide and never serve stale.
 */
export type BrandingAssetRole = 'logo-icon' | 'favicon' | 'og';

/** Extensions for content types the branding uploads are expected to carry. */
const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/x-icon': 'ico',
    'image/vnd.microsoft.icon': 'ico',
};

function extensionFromContentType(contentType: string | null): string | null {
    return contentType ? (CONTENT_TYPE_EXTENSIONS[contentType] ?? null) : null;
}

function extensionFromName(name: string): string | null {
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === name.length - 1) return null;
    const ext = name.slice(lastDot + 1).toLowerCase();
    return /^[a-z0-9]+$/.test(ext) ? ext : null;
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Uploads realm branding assets into the platform's shared content store
 * (Admin API `uploadSharedContent`). Returns the store's *relative* URL —
 * that is what goes into the realm branding config: every realm serves the
 * shared store from its own origin, and the OG image is absolutized
 * server-side during index.html templating. Requires a global admin.
 */
@Injectable({ providedIn: 'root' })
export class SharedContentService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    /**
     * Uploads an operator-picked file. Returns the relative URL, or null on
     * failure (reported via notifications).
     */
    async uploadBrandingFile(
        realmId: string,
        role: BrandingAssetRole,
        file: File,
    ): Promise<string | null> {
        const ext =
            extensionFromContentType(file.type || null) ??
            extensionFromName(file.name);
        let contentBase64: string;
        try {
            contentBase64 = await blobToBase64(file);
        } catch {
            this.notification.show({
                severity: 'error',
                summary: 'Upload failed',
                detail: 'Could not read the selected file.',
            });
            return null;
        }
        return this.upload(
            realmId,
            role,
            contentBase64,
            file.type || null,
            ext,
        );
    }

    /**
     * Re-hosts an image living on a foreign store (an AI Assist candidate)
     * into the shared content store. The realm config must never reference
     * the AI service's bucket — its objects are working artifacts with no
     * retention guarantee. The source must allow cross-origin GET.
     */
    async rehostBrandingUrl(
        realmId: string,
        role: BrandingAssetRole,
        sourceUrl: string,
    ): Promise<string | null> {
        let blob: Blob;
        try {
            const response = await fetch(sourceUrl, { cache: 'no-store' });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            blob = await response.blob();
        } catch {
            this.notification.show({
                severity: 'error',
                summary: 'Could not copy the generated image',
                detail: 'Fetching the image from the AI service storage failed — the branding field was not changed.',
            });
            return null;
        }

        const contentType = blob.type || null;
        const ext =
            extensionFromContentType(contentType) ??
            extensionFromName(new URL(sourceUrl, location.href).pathname);
        let contentBase64: string;
        try {
            contentBase64 = await blobToBase64(blob);
        } catch {
            this.notification.show({
                severity: 'error',
                summary: 'Could not copy the generated image',
                detail: 'Reading the downloaded image failed — the branding field was not changed.',
            });
            return null;
        }
        return this.upload(realmId, role, contentBase64, contentType, ext);
    }

    /**
     * Uploads an operator-picked image into the shared content store at
     * `${basePath}.{ext}` (the store inserts a content hash before the
     * extension, so re-uploads never collide and never serve stale). Returns
     * the store's relative URL, or null on failure (reported via
     * notifications). For non-branding asset editors that key their own path
     * (e.g. product base art). Restricted to global admins on the backend.
     */
    async uploadImageFile(
        basePath: string,
        file: File,
    ): Promise<string | null> {
        const ext =
            extensionFromContentType(file.type || null) ??
            extensionFromName(file.name);
        let contentBase64: string;
        try {
            contentBase64 = await blobToBase64(file);
        } catch {
            this.notification.show({
                severity: 'error',
                summary: 'Upload failed',
                detail: 'Could not read the selected file.',
            });
            return null;
        }
        const filePath = ext ? `${basePath}.${ext}` : basePath;
        try {
            const resp = await this.api.client.call(
                contentApi.uploadSharedContent({
                    FilePath: filePath,
                    ContentBase64: contentBase64,
                    ContentType: file.type || null,
                    UseExactPath: false,
                }),
            );
            return resp.RelativeUrl;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    /**
     * Re-hosts an image from a foreign store (e.g. an AI Assist candidate URL)
     * into the shared content store at `${basePath}.{ext}`. Returns the relative
     * URL, or null on failure (reported via notifications). The source must
     * allow cross-origin GET. Mirrors `rehostBrandingUrl` but keys an arbitrary
     * path (e.g. product base art).
     */
    async rehostImageUrl(
        basePath: string,
        sourceUrl: string,
    ): Promise<string | null> {
        let blob: Blob;
        try {
            const response = await fetch(sourceUrl, { cache: 'no-store' });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            blob = await response.blob();
        } catch {
            this.notification.show({
                severity: 'error',
                summary: 'Could not copy the generated image',
                detail: 'Fetching the image from the AI service storage failed.',
            });
            return null;
        }
        const file = new File([blob], 'art', {
            type: blob.type || 'image/webp',
        });
        return this.uploadImageFile(basePath, file);
    }

    private async upload(
        realmId: string,
        role: BrandingAssetRole,
        contentBase64: string,
        contentType: string | null,
        ext: string | null,
    ): Promise<string | null> {
        const fileName = ext ? `${role}.${ext}` : role;
        try {
            const resp = await this.api.client.call(
                contentApi.uploadSharedContent({
                    FilePath: `branding/${realmId}/${fileName}`,
                    ContentBase64: contentBase64,
                    ContentType: contentType,
                    UseExactPath: false,
                }),
            );
            return resp.RelativeUrl;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }
}
